import { Controller } from "@hotwired/stimulus"
import Hls from "hls.js"
import { PlaybackSpeed } from "./playback_speed"

// Connects to data-controller="hls-player"
// Properly initializes hls.js before the native player can claim the video element
// Uses custom controls to match the S3 standalone player (fixes Windows caption overlap)
export default class extends Controller {
  static targets = ["video", "vidInfo", "fragPlaying", "fragLoaded", "levelInfo", "qLevels", "levelInfo2", "errorInfo", "errorGroup", "resizeHandler", "controls", "playBtn", "progress", "timeDisplay", "muteBtn", "volSlider", "ccBtn", "speedBtn", "fsBtn"]
  static values = {
    url: String,
    videoId: Number
  }

  connect() {
    console.log("hls-player controller connected", this.urlValue || "no url")
    if (!this.hasVideoTarget || !this.urlValue) return

    this.ccOn = true
    this.initPlayer()
    this.initResizeDragger()
  }

  disconnect() {
    if (this.hls) {
      this.hls.destroy()
      this.hls = null
    }
  }

  initPlayer() {
    const video = this.videoTarget

    if (Hls.isSupported()) {
      const config = {
        capLevelToPlayerSize: true,
        startLevel: -1,
        // Safari ignores Vary: Origin, so S3 responses cached same-origin (by
        // a visit to the standalone S3 player) get served to our cross-origin
        // XHRs without CORS headers -> manifestLoadError. A query param keeps
        // this page's cache entries in their own namespace.
        xhrSetup: (xhr, url) => {
          const sep = url.includes("?") ? "&" : "?"
          xhr.open("GET", `${url}${sep}xo=1`, true)
        }
      }
      this.hls = new Hls(config)
      window.hls = this.hls

      this.setupCallbacks(this.hls)
      this.hls.loadSource(this.urlValue)
      this.hls.attachMedia(video)

      // Build custom controls AFTER hls.js has attached
      this.hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        this.buildCustomControls()
      })

    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS support (same cache-namespace param as above)
      video.src = this.urlValue + (this.urlValue.includes("?") ? "&" : "?") + "xo=1"
      video.addEventListener('loadedmetadata', () => {
        this.buildCustomControls()
      })
    }

    window.player = video
  }

  buildCustomControls() {
    const video = this.videoTarget
    const container = video.parentElement

    // Wrap video in a player container if not already wrapped
    let playerContainer = container.querySelector('.vips-player-container')
    if (!playerContainer) {
      playerContainer = document.createElement('div')
      playerContainer.className = 'vips-player-container'
      playerContainer.id = 'player-container'
      video.parentNode.insertBefore(playerContainer, video)
      playerContainer.appendChild(video)
    }

    // Create control bar
    const controls = document.createElement('div')
    controls.className = 'vips-controls'
    controls.innerHTML = `
      <button class="vips-btn" data-hls-player-target="playBtn" title="Play">&#9658;</button>
      <div class="vips-progress-wrap" data-action="click->hls-player#seek">
        <div class="vips-progress" data-hls-player-target="progress"></div>
      </div>
      <span class="vips-time" data-hls-player-target="timeDisplay">0:00 / 0:00</span>
      <div class="vips-volume">
        <button class="vips-btn" data-hls-player-target="muteBtn" title="Mute">&#128266;</button>
        <input type="range" data-hls-player-target="volSlider" min="0" max="1" step="0.1" value="1">
      </div>
      <button class="vips-btn" data-hls-player-target="ccBtn" title="Captions">CC</button>
      <button type="button" class="vips-btn vips-speed" data-hls-player-target="speedBtn" title="Playback speed (&lt; and &gt; keys)" aria-label="Playback speed 1×">1×</button>
      <button class="vips-btn" data-hls-player-target="fsBtn" title="Fullscreen">&#9974;</button>
    `
    playerContainer.appendChild(controls)

    // Set up event listeners
    this.setupCustomControlEvents()
  }

  setupCustomControlEvents() {
    const video = this.videoTarget

    // Play/Pause
    if (this.hasPlayBtnTarget) {
      this.playBtnTarget.addEventListener('click', () => this.togglePlay())
    }
    video.addEventListener('click', () => this.togglePlay())
    video.addEventListener('play', () => {
      if (this.hasPlayBtnTarget) this.playBtnTarget.innerHTML = '&#10074;&#10074;'
    })
    video.addEventListener('pause', () => {
      if (this.hasPlayBtnTarget) this.playBtnTarget.innerHTML = '&#9658;'
    })

    // Progress
    video.addEventListener('timeupdate', () => this.updateProgress())

    // Volume
    if (this.hasMuteBtnTarget) {
      this.muteBtnTarget.addEventListener('click', () => this.toggleMute())
    }
    video.addEventListener('volumechange', () => this.updateVolumeUI())
    if (this.hasVolSliderTarget) {
      this.volSliderTarget.addEventListener('input', (e) => {
        video.volume = e.target.value
        video.muted = (e.target.value == 0)
      })
    }

    // Captions
    if (this.hasCcBtnTarget) {
      this.ccBtnTarget.addEventListener('click', () => this.toggleCaptions())
      // Check for captions after a short delay
      setTimeout(() => {
        if (video.textTracks.length) {
          this.ccBtnTarget.classList.add('cc-on')
        } else {
          this.ccBtnTarget.style.display = 'none'
        }
      }, 500)
    }

    // Playback speed (same behavior as the standalone player)
    this.speed = new PlaybackSpeed(video, this.hasSpeedBtnTarget ? this.speedBtnTarget : null)
    if (this.hasSpeedBtnTarget) {
      this.speedBtnTarget.addEventListener('click', () => this.speed.cycle())
    }

    // Fullscreen
    if (this.hasFsBtnTarget) {
      this.fsBtnTarget.addEventListener('click', () => this.toggleFullscreen())
    }

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => this.handleKeyboard(e))
  }

  togglePlay() {
    const video = this.videoTarget
    video.paused ? video.play() : video.pause()
  }

  updateProgress() {
    const video = this.videoTarget
    if (video.duration && this.hasProgressTarget) {
      this.progressTarget.style.width = (video.currentTime / video.duration * 100) + '%'
    }
    if (this.hasTimeDisplayTarget) {
      this.timeDisplayTarget.textContent = this.formatTime(video.currentTime) + ' / ' + this.formatTime(video.duration)
    }
  }

  seek(e) {
    const video = this.videoTarget
    const rect = e.currentTarget.getBoundingClientRect()
    const pct = (e.clientX - rect.left) / rect.width
    video.currentTime = pct * video.duration
  }

  toggleMute() {
    this.videoTarget.muted = !this.videoTarget.muted
  }

  updateVolumeUI() {
    const video = this.videoTarget
    if (this.hasMuteBtnTarget) {
      this.muteBtnTarget.innerHTML = (video.muted || video.volume === 0) ? '&#128263;' : '&#128266;'
    }
    if (this.hasVolSliderTarget) {
      this.volSliderTarget.value = video.muted ? 0 : video.volume
    }
  }

  toggleCaptions() {
    const video = this.videoTarget
    const tracks = video.textTracks
    if (tracks.length) {
      this.ccOn = !this.ccOn
      for (let i = 0; i < tracks.length; i++) {
        tracks[i].mode = this.ccOn ? 'showing' : 'hidden'
      }
      if (this.hasCcBtnTarget) {
        this.ccBtnTarget.classList.toggle('cc-on', this.ccOn)
      }
    }
  }

  toggleFullscreen() {
    const container = this.videoTarget.closest('.vips-player-container')
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else if (container) {
      container.requestFullscreen()
    }
  }

  handleKeyboard(e) {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return

    const video = this.videoTarget
    switch(e.key) {
      case ' ':
      case 'k':
        e.preventDefault()
        this.togglePlay()
        break
      case 'ArrowLeft':
        e.preventDefault()
        video.currentTime -= 5
        break
      case 'ArrowRight':
        e.preventDefault()
        video.currentTime += 5
        break
      case 'm':
        this.toggleMute()
        break
      case '>':
        e.preventDefault()
        if (this.speed) this.speed.step(1)
        break
      case '<':
        e.preventDefault()
        if (this.speed) this.speed.step(-1)
        break
      case 'c':
        this.toggleCaptions()
        break
      case 'f':
        this.toggleFullscreen()
        break
    }
  }

  formatTime(s) {
    if (isNaN(s)) return '0:00'
    const m = Math.floor(s / 60)
    const sec = Math.floor(s % 60)
    return m + ':' + (sec < 10 ? '0' : '') + sec
  }

  setupCallbacks(hls) {
    const video = this.videoTarget

    video.addEventListener('timeupdate', (event) => {
      let currentTime = event.target.currentTime.toFixed(2)
      let dur = event.target.duration.toFixed(2)
      if (dur === "NaN") dur = "0.00"
      if (this.hasVidInfoTarget) {
        this.vidInfoTarget.innerText = "Playback Time: " + currentTime + " of " + dur
        this.updatePlayerSize()
      }
    })

    video.addEventListener('ended', () => {
      if (this.hasFragPlayingTarget) {
        this.fragPlayingTarget.innerText = "Playback complete"
      }
    })

    hls.on(Hls.Events.ERROR, (d1, d2) => {
      if (d2.fatal) {
        if (this.hasErrorGroupTarget) this.errorGroupTarget.classList.add("show")
        console.log("FATAL HLS ERROR", d1, d2)
        if (this.hasErrorInfoTarget) {
          this.errorInfoTarget.innerText = `FATAL HLS ERROR: ${d2.type} - ${d2.details}`
        }
      }
    })

    hls.on(Hls.Events.STREAM_STATE_TRANSITION, (d1, d2) => {
      console.log("STREAM_STATE_TRANSITION", d1, d2)
    })

    hls.on(Hls.Events.MANIFEST_LOADING, () => {
      if (this.hasFragLoadedTarget) {
        this.fragLoadedTarget.innerText = "Manifest loading..."
      }
    })

    hls.on(Hls.Events.MANIFEST_LOADED, (event, data) => {
      if (this.hasFragLoadedTarget) {
        this.fragLoadedTarget.innerText = "Manifest loaded. Quality level count: " + data.levels.length
      }
    })

    hls.on(Hls.Events.LEVEL_SWITCHED, (data, f2) => {
      let cl = hls.levels[hls.currentLevel]
      let ih = "<span>VIDEO QUALITY LEVELS: </span><div class='btn-group ps-1'>"
      for (let i = 0; i < hls.levels.length; i++) {
        let isPrimary = f2.level === i
        let pTag = isPrimary ? " active " : ""
        ih += "<a onclick='window.hls.currentLevel = " + i + ";' class='btn btn-outline-secondary btn-sm " + pTag + "'>" + i + "</a>"
      }
      ih += "</div>"
      if (this.hasQLevelsTarget) this.qLevelsTarget.innerHTML = ih
      if (this.hasLevelInfo2Target) {
        this.levelInfo2Target.innerText = "Quality bandwidth requirement: " + (cl.bitrate / 1000) + "/kbs - Estimated bandwidth available: " + ((hls.bandwidthEstimate / 1000).toFixed(0)) + "/kbs"
      }
    })

    hls.on(Hls.Events.FRAG_LOADED, (data, f2) => {
      if (hls.currentLevel < 0) return
      let totalFragments = hls.levels[hls.currentLevel].details.fragments.length
      if (this.hasFragLoadedTarget) {
        if (f2.frag.sn + 1 === totalFragments) {
          this.fragLoadedTarget.innerText = "Loaded all HLS fragments"
        } else {
          this.fragLoadedTarget.innerText = "Loaded HLS fragment #" + (f2.frag.sn + 1) + " of " + totalFragments
        }
      }
    })

    hls.on(Hls.Events.FRAG_CHANGED, (data, f2) => {
      let totalLevels = hls.levels[hls.currentLevel].details.fragments.length
      if (this.hasFragPlayingTarget) {
        this.fragPlayingTarget.innerText = "Displaying fragment #" + (f2.frag.sn + 1) + " of " + totalLevels
      }
      if (this.hasLevelInfoTarget) {
        this.levelInfoTarget.innerText = " of quality level " + hls.currentLevel + "."
      }
    })
  }

  updatePlayerSize() {
    const video = this.videoTarget
    let bounds = video.getBoundingClientRect()
    let sizeStr = " - Player size: " + (Math.round(bounds.width * 10) / 10 + " x " + Math.round(bounds.height * 10) / 10)
    if (video.videoWidth && video.videoHeight) {
      sizeStr += " - Video stream size: " + (video.videoWidth + " x " + video.videoHeight)
    }
    if (this.hasVidInfoTarget) {
      this.vidInfoTarget.innerText += sizeStr
    }
  }

  initResizeDragger() {
    if (!this.hasResizeHandlerTarget) return

    const container = this.element.querySelector('.video-container1')
    const handler = this.resizeHandlerTarget
    handler.style.setProperty('cursor', 'ns-resize', 'important')
    let startY, startHeight

    handler.addEventListener('mousedown', (e) => {
      startY = e.clientY
      startHeight = parseInt(document.defaultView.getComputedStyle(container).height, 10)
      document.documentElement.addEventListener('mousemove', drag)
      document.documentElement.addEventListener('mouseup', stop)
    })

    const drag = (e) => {
      container.style.height = (startHeight + e.clientY - startY) + 'px'
    }

    const stop = () => {
      document.documentElement.removeEventListener('mousemove', drag)
      document.documentElement.removeEventListener('mouseup', stop)
    }
  }
}
